import { useState, useEffect, useRef } from "react";
import { useClientUserState } from '../../../Contexts/ClientUserStateContext';
import { useGlobalState } from "../../../Contexts/GlobalStateContext";
import { Form } from "react-bootstrap";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import DashboardHeader from "../../../Components/DashboardHeader/DashboardHeader";
import SubTitle from "../../../Components/SubTitle/SubTitle";
import CheckoutForm from "../../../Components/CheckoutForm/CheckoutForm";
import Buttons from "../../../Components/Buttons/Buttons";
import PaymentHistory from '../../../Components/AccountTable/AccountTable';
import Popup from "../../../Components/Popup/Popup";
import { useApi } from '../../../Services/api';
import './AccountBilling.css';
import backgroundImg from '../../../Images/NoFoundImg.png';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const AccountBilling = () => {
  const [validated, setValidated] = useState(false);
  const [cardDetails, setCardDetails] = useState({
    cardType: '',
    cardNumber: '',
    expMonth: '',
    expYear: '',
  });
  const [loaded, setLoaded] = useState(null);
  const [intentId, setIntentId] = useState("");
  const [stripeCustomerId, setStripeCustomerId] = useState("");
  const [expMonth, setExpMonth] = useState("");
  const [expYear, setExpYear] = useState("");
  const [cardHolderName, setCardHolderName] = useState('');
  const [cardHolderNameValid, setCardHolderNameValid] = useState(false);
  const [creditCardValid, setCreditCardValid] = useState(false);
  const [cardLast4, setCardLast4] = useState('');
  const checkoutFormRef = useRef();
  const [clientSecret, setClientSecret] = useState("");
  const [creditCardError, setCreditCardError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [paymentHistoryTable, setPaymentHistoryTable] = useState([]);
  const [isDisable, setIsDisable] = useState(true);
  const columns = ["Date", "Payment Methods", "Price"];
  const [showSuccess, setShowSuccess] = useState(false);
  const { clientUser } = useClientUserState();
  const { setErrorMessage, setErrorVisible, setLoading,  } = useGlobalState();
  const api = useApi();

  const handleError = (message) => {
    setCreditCardError(message);
  };

  const handleCardValidation = (isValid, last4) => {
    setCreditCardValid(isValid);
    if (last4) {
      setCardLast4(last4);
    }
    if (!cardHolderName) {
      setCreditCardError('Invalid credit card information!');
    } else {
      setCreditCardError('');
    }
  };

  const handleClose = async() => {
    setShowSuccess(false);
  }

  useEffect(() => {
    // if not an admin, don't show this page
    if (!clientUser.IsAdmin) {
      window.location.href = '/ClientSignUp/';
    }
    const fetchData = async () => {
      try {
        const responses = await Promise.all([
          api.get('Client/create-setup-intent'),
          api.get('Client/GetPayment'),
          api.get('Client/GetPaymentHistory')
        ]);
        setLoading(true);

        // Setup Intent
        const stripeData = responses[0].data;
        setIntentId(stripeData.IntentId);
        setStripeCustomerId(stripeData.CustomerId);
        setClientSecret(stripeData.ClientSecret);

        // Payment Data
        const paymentData = responses[1].data;
        setCardDetails({
          cardType: paymentData.DisplayBrand,
          cardNumber: paymentData.MaskedCreditCardNumber,
          expMonth: paymentData.ExpMonth,
          expYear: paymentData.ExpYear
        });
        setCardHolderName(paymentData.CardHolderName);

        // Payment History
        const paymentHistoryData = responses[2].data;
        const data = paymentHistoryData.Payments.map((item) => {
          const [datePart, timePart] = item.Date.split('T');
          return {
            date: (
              <>
                <span>{datePart}</span><br />
                <span>T{timePart}</span>
              </>
            ),
            payment_methods: `${item.Brand} **** **** **** ${item.Last4}`,
            price: `$${item.Price}`
          };
        });
        setPaymentHistoryTable(data);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
        setLoaded(true);
      }
    };

    fetchData();
  }, []);

  const appearance = { theme: "stripe" };
  const options = { clientSecret, appearance };
  const handleEdit = () => {
    setIsDisable(!isDisable);
  }
  const handleCancel = () => {
    setIsDisable(!isDisable);
  }
  const updatePayment = async (newPaymentInfo) => {
    const paymentData = {
      CardHolderName: cardHolderName,
      StripeCustomerId: stripeCustomerId,
      IntentId: intentId,
      PaymentMethod: newPaymentInfo.paymentMethod,
      DisplayBrand: newPaymentInfo.displayBrand,
      ExpMonth: newPaymentInfo.expMonth,
      ExpYear: newPaymentInfo.expYear,
      Funding: newPaymentInfo.funding,
      Last4: newPaymentInfo.last4,
    };
    try {
      const raw = await api.post('Client/PaymentUpdate', paymentData);
      const updateData = raw.data;
      if (!updateData.Success) {
        setErrorMessage(updateData.Message);
        setErrorVisible(true);
      } else {
        setShowSuccess(true);
      }
    } catch (error) {
      console.log('error:', error);
    }
  }


  const handleConfirm = async (event) => {
    event.preventDefault();
    setSubmitted(true);
    if (!cardHolderName) {
      setCardHolderNameValid(true);
      return;
    } else {
      setCardHolderNameValid(false);
    }
    if (!checkoutFormRef.current) return;
    let ref;
    try {
      ref = await checkoutFormRef.current.handleCreditSubmit();
    } catch (error) {
      console.log('error', error)
      return
    }

    
    if (cardHolderName && ref.validation && ref.id) {
      const newPaymentInfo = {
        paymentMethod: ref.id,
        displayBrand: ref.card.display_brand,
        expMonth: ref.card.exp_month,
        expYear: ref.card.exp_year,
        funding: ref.card.funding,
        last4: ref.card.last4
      };

      setCardDetails({
        cardType: newPaymentInfo.displayBrand,
        cardNumber: `**** **** **** ${newPaymentInfo.last4}`,
        expMonth: newPaymentInfo.expMonth,
        expYear: newPaymentInfo.expYear
      });
      await updatePayment(newPaymentInfo);
    }
    setIsDisable(!isDisable);
  }

  if (!loaded) {
    return null;
  } else if (loaded === false) { 
    return (
      <h1>Failed to load</h1>
    )
  }

  return (
    <div className="billing-container">
      <DashboardHeader title="Billing and Payments" />
      <SubTitle subTitle="Information" />
      <div className="form-container">
        <Form className="billing-form" noValidate validated={validated}>
          <Form.Group controlId="validationCustom01">
            <div className='form-group-inline'>
              <Form.Label>Cardholder Name*</Form.Label>
              <div className="invalid-feedback" style={{ display: cardHolderNameValid ? 'block' : 'none' }}>
                required field
              </div>
            </div>
            <Form.Control
              required
              disabled = {isDisable}
              type="text"
              placeholder="Enter the cardholder name"
              value={cardHolderName}
              onChange={(e) => setCardHolderName(e.target.value)}
              className="step-inputBox"
            />
          </Form.Group>
          <div>
            <div className="form-group-inline">
              <Form.Label>Credit Card*</Form.Label>
              {submitted && !creditCardValid && <div className="invalid-feedback" style={{ display: 'block' }}>{creditCardError}</div>}
            </div>
            {clientSecret && !isDisable && (
              <Elements stripe={stripePromise} options={options}>
                <CheckoutForm required isFilled={true} cardDetails={cardDetails} disabled={isDisable} onCardValidation={handleCardValidation} onError={handleError} ref={checkoutFormRef} />
              </Elements>
            )}
            { isDisable && (
              <div className="billing-manual-disable">
                <div className="billing-cardNumber">**** **** **** {cardDetails.cardNumber}</div>
                <div className="billing-extra-details">
                  <div className="billing-expiry">{cardDetails.expMonth}/{cardDetails.expYear}</div>
                  <div className="billing-csv">***</div>
                </div> 
              </div>
            )}
          </div>
          <div className="billing-button">
            {isDisable ? <Buttons text="Edit" handleSubmit={handleEdit} /> :
              <div>
                <Buttons back backText="Cancel" handleBack={handleCancel} text="Confirm" handleSubmit={handleConfirm}/>
              </div>
            }
          </div>
        </Form>
      </div>
      <SubTitle subTitle="Payment History" />
      {paymentHistoryTable.length === 0 ? (
        <>
        <div className="table-noFound-container">
        <img src={backgroundImg} alt="Accountbilling-backgroundImg" ></img>
        <h5 className="no-payment-history">No payment history available</h5>
        </div>
        </>
      ) :
      <>
      <div className="Payment-history-table">
        <PaymentHistory
          data={paymentHistoryTable}
          columns={columns}
        />
        </div>
        </>
      }
      {showSuccess && (
        <Popup 
          show={showSuccess}
          handleClose={handleClose}
          title={'Payment Update Successful'}
          titleStyle="popup-title"
          body={
            <>
              <p>Your payment method has been successfully updated.</p>
              <div className='Login-checkEmail-pop-btn'>
                <Buttons text={'Done'} handleSubmit={handleClose} />
              </div>
            </>
          }
        />
      )}
    </div>
  );
};

export default AccountBilling;
